import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Eyebrow,
  Pill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components';
import { useApi, toApiError, type OutboxDeadLetterItem } from '@/shared/api';
import { formatDateTime } from '@/shared/lib/format';

/**
 * Dead-Letter-Recovery für ausgehende Preis- und Verfügbarkeits-Pushes an den Kanalmanager.
 * Endgültig gescheiterte Pushes werden hier gelistet; „Erneut senden" setzt Versuche, Wartezeit
 * und Dead-Letter-Marke zurück — der Worker nimmt die Nachricht beim nächsten Durchlauf wieder auf.
 */
const SOURCE_LABELS: Record<OutboxDeadLetterItem['source'], string> = {
  single: 'Einzeln',
  batch: 'Sammel',
};

export function ChannexDeadLetterSection() {
  const api = useApi();
  const qc = useQueryClient();
  const [resendingId, setResendingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['system', 'channex-deadletter'],
    queryFn: () => api.channex.deadLetterList(),
  });

  const resend = useMutation({
    mutationFn: (item: OutboxDeadLetterItem) => api.channex.resendDeadLetter(item.source, item.id),
    onSuccess: () => {
      toast.success('Push erneut eingereiht');
      void qc.invalidateQueries({ queryKey: ['system', 'channex-deadletter'] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
    onSettled: () => setResendingId(null),
  });

  const items = data ?? [];

  return (
    <section className="mt-12">
      <Eyebrow variant="muted">Kanalmanager</Eyebrow>
      <h2 className="text-ink mb-1 mt-2 font-serif text-2xl">
        Endgültig gescheiterte <span className="font-serif-italic">Pushes</span>
        <span className="text-sealwax">.</span>
      </h2>
      <p className="text-ink-soft mb-4 max-w-prose text-sm">
        Ausgehende Preis-, Verfügbarkeits- und Inhalts-Pushes, die nach allen Wiederholungen
        aufgegeben haben — über <em>alle</em> Workspaces des Kanalmanager-Kontos. „Erneut senden"
        stellt sie zurück in die Warteschlange; abgearbeitet werden sie im Workspace ihrer Zeile.
      </p>

      {isLoading ? (
        <p className="text-ink-soft text-sm">Wird geladen…</p>
      ) : items.length === 0 ? (
        <p className="text-ink-soft text-sm">
          Kein gescheiterter Push — hier steht im Normalfall nichts.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Art</TableHead>
              <TableHead>Workspace</TableHead>
              <TableHead>Apartment</TableHead>
              <TableHead className="text-right">Versuche</TableHead>
              <TableHead>Fehler</TableHead>
              <TableHead>Aufgegeben</TableHead>
              <TableHead>
                <span className="sr-only">Aktion</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((m) => (
              <TableRow key={m.id}>
                <TableCell>
                  <Pill variant="outline" size="sm">
                    {SOURCE_LABELS[m.source]}
                  </Pill>
                </TableCell>
                <TableCell className="text-sm">{m.workspaceName}</TableCell>
                <TableCell className="text-ink-soft font-mono text-xs">
                  {m.propertyId.slice(0, 8)}…
                </TableCell>
                <TableCell className="text-right tabular-nums">{m.attempts}</TableCell>
                <TableCell
                  className="text-sealwax max-w-md truncate text-xs"
                  title={m.lastError ?? ''}
                >
                  {m.lastError ?? '—'}
                </TableCell>
                <TableCell className="text-ink-soft whitespace-nowrap font-mono text-xs">
                  {m.deadLetteredAt ? formatDateTime(m.deadLetteredAt) : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    className="min-h-11"
                    loading={resendingId === m.id}
                    onClick={() => {
                      setResendingId(m.id);
                      resend.mutate(m);
                    }}
                  >
                    Erneut senden
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
